import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { type ImportedLocation } from 'api/location-importer.api';
import { type HaulRoute } from 'api/haul-route.api';
import { type BlockedRoadEntry } from './roadsImporter.constants';
import {
  blockedTtlMinutesRemaining,
  formatRoadName,
} from './roadsImporter.helpers';

export interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  location: ImportedLocation | null;
  route: HaulRoute | null;
  pendingRouteIndex: number | null;
}

interface RoadsImporterContextMenuProps {
  contextMenu: ContextMenuState | null;
  // Other routes physically overlapping contextMenu.route (see
  // findOverlappingRoutes) — the reverse leg of a two-way road, most often.
  // Right-click hit-testing can only ever resolve to one route when several
  // occupy the same cells, so "Edit Road" lists all of them by name instead
  // of silently always editing the same one.
  overlappingRoutes: HaulRoute[];
  onClose: () => void;
  onEditNode: (loc: ImportedLocation) => void;
  onMoveCentroid: (loc: ImportedLocation) => void;
  onRoutesFromNode: (loc: ImportedLocation | null) => void;
  onDrawRoadFrom: (loc: ImportedLocation | null) => void;
  onDeleteNode: (loc: ImportedLocation | null) => void;
  onChangePendingWidth: (index: number) => void;
  onViewRoute: (route: HaulRoute | null) => void;
  onEditRoute: (route: HaulRoute) => void;
  onEditRoutePath: (route: HaulRoute) => void;
  onToggleRouteActive: (route: HaulRoute) => void;
  onBlockRoute: (route: HaulRoute) => void;
  onUnblockRoute: (route: HaulRoute) => void;
  onDeleteRoute: (route: HaulRoute | null) => void;
  onAddLocation: () => void;
  onMapGradeBlocks: (loc: ImportedLocation) => void;
  blockedRoads: Map<string, BlockedRoadEntry>;
}

// Right-click menu on the Mine Map Studio map — three mutually-exclusive
// branches (location hit / pending-route hit / saved-route hit) plus the
// empty-ground "Add Location" fallback. Which branch renders is resolved
// by the caller's hit-test priority and passed in via `contextMenu`.
export function RoadsImporterContextMenu({
  contextMenu,
  overlappingRoutes,
  onClose,
  onEditNode,
  onMoveCentroid,
  onRoutesFromNode,
  onDrawRoadFrom,
  onDeleteNode,
  onChangePendingWidth,
  onViewRoute,
  onEditRoute,
  onEditRoutePath,
  onToggleRouteActive,
  onBlockRoute,
  onUnblockRoute,
  onDeleteRoute,
  onAddLocation,
  onMapGradeBlocks,
  blockedRoads,
}: RoadsImporterContextMenuProps): JSX.Element {
  const { t } = useTranslation();
  const blockedInfo = contextMenu?.route
    ? blockedRoads.get(contextMenu.route.routeId)
    : undefined;
  const blockedTtlMinutes = blockedInfo
    ? blockedTtlMinutesRemaining(blockedInfo.ttlSeconds)
    : null;

  // Fly-out: "Edit Road"/"View Road Details" themselves never fire an
  // action when there's more than one road to choose from — clicking the
  // item opens a nested menu, anchored to it, listing each overlapping
  // road. A single slot (not one anchor per action) so opening one always
  // closes whichever other fly-out was open, instead of stacking both.
  const [activeSubmenu, setActiveSubmenu] = useState<{
    key: string;
    anchor: HTMLElement;
  } | null>(null);

  // A right-click anywhere else on the map replaces `contextMenu` directly
  // (see the view's click handler) without ever calling onClose/closeAll in
  // between — left stale, the anchor from the previous menu would make the
  // submenu appear already open on the next one, with no hover needed.
  useEffect(() => {
    setActiveSubmenu(null);
  }, [contextMenu]);

  const closeAll = () => {
    setActiveSubmenu(null);
    onClose();
  };

  // Renders a route-scoped menu item that either fires directly (a plain
  // one-way road) or, when overlappingRoutes is non-empty, expands into a
  // fly-out listing every road sharing this point so the user can pick
  // which one the action applies to.
  const renderRoutePickerItem = (
    itemKey: string,
    icon: JSX.Element,
    labelKey: string,
    onSelect: (route: HaulRoute) => void
  ): JSX.Element | null => {
    if (!contextMenu?.route) return null;
    if (overlappingRoutes.length === 0) {
      return (
        <MenuItem
          key={itemKey}
          onClick={() => {
            onSelect(contextMenu.route as HaulRoute);
            closeAll();
          }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{t(labelKey)}</ListItemText>
        </MenuItem>
      );
    }
    const submenuOpen = activeSubmenu?.key === itemKey;
    return (
      <MenuItem
        key={itemKey}
        onClick={(e) => {
          setActiveSubmenu({ key: itemKey, anchor: e.currentTarget });
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{t(labelKey)}</ListItemText>
        <ChevronRightIcon
          fontSize="small"
          sx={{ ml: 'auto', pl: 2, opacity: 0.6 }}
        />
        <Menu
          open={submenuOpen}
          anchorEl={submenuOpen ? activeSubmenu.anchor : null}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={() => {
            setActiveSubmenu(null);
          }}
          hideBackdrop
          // Hiding the backdrop above isn't enough on its own: MUI's Modal
          // renders its own full-viewport wrapper <div> regardless (a plain
          // transparent element, but pointer-events: auto by default still
          // eats every click over the whole screen). Disable pointer events
          // on that wrapper and re-enable them only on the visible menu
          // paper so clicks on the outer menu's other items pass through.
          sx={{ pointerEvents: 'none' }}
          PaperProps={{ sx: { pointerEvents: 'auto' } }}
        >
          {[contextMenu.route, ...overlappingRoutes].map((route) => (
            <MenuItem
              key={route.routeId}
              onClick={() => {
                onSelect(route);
                closeAll();
              }}
            >
              <ListItemText>
                {formatRoadName(route.roadName ?? route.routeKey)}
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </MenuItem>
    );
  };

  return (
    <Menu
      open={contextMenu !== null}
      onClose={closeAll}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      {contextMenu?.location ? (
        [
          <MenuItem
            key="add-location"
            onClick={() => {
              onAddLocation();
              closeAll();
            }}
          >
            <ListItemIcon>
              <AddLocationAltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_add_location')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="edit-node"
            onClick={() => {
              if (contextMenu.location) onEditNode(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_edit_node')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="move-centroid"
            onClick={() => {
              if (contextMenu.location) onMoveCentroid(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <LocationOnOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t('roads_importer_menu_move_centroid')}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="routes-from"
            onClick={() => {
              onRoutesFromNode(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <AltRouteOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_routes_from')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="draw-road"
            onClick={() => {
              onDrawRoadFrom(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <RouteOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_draw_road')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="map-grade-blocks"
            onClick={() => {
              if (contextMenu.location) onMapGradeBlocks(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <LayersOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t('roads_importer_menu_map_grade_blocks')}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="delete-node"
            onClick={() => {
              onDeleteNode(contextMenu.location);
              closeAll();
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>
              {t('roads_importer_menu_delete_node')}
            </ListItemText>
          </MenuItem>,
        ]
      ) : contextMenu?.pendingRouteIndex != null ? (
        [
          <MenuItem
            key="add-location"
            onClick={() => {
              onAddLocation();
              closeAll();
            }}
          >
            <ListItemIcon>
              <AddLocationAltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_add_location')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="change-width"
            onClick={() => {
              const idx = contextMenu.pendingRouteIndex;
              if (idx != null) onChangePendingWidth(idx);
              closeAll();
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_change_width')}</ListItemText>
          </MenuItem>,
        ]
      ) : contextMenu?.route ? (
        [
          ...(blockedInfo
            ? [
                <Box key="blocked-info" sx={{ px: 2, py: 1, maxWidth: 280 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'error.main',
                      display: 'block',
                    }}
                  >
                    {blockedTtlMinutes == null
                      ? t('roads_importer_context_blocked_indefinite')
                      : t('roads_importer_context_blocked_countdown', {
                          minutes: blockedTtlMinutes,
                        })}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {t('roads_importer_field_block_reason')}:{' '}
                    {blockedInfo.reason}
                  </Typography>
                </Box>,
                <Divider key="blocked-divider" />,
              ]
            : []),
          <MenuItem
            key="add-location"
            onClick={() => {
              onAddLocation();
              closeAll();
            }}
          >
            <ListItemIcon>
              <AddLocationAltOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roads_importer_menu_add_location')}</ListItemText>
          </MenuItem>,
          renderRoutePickerItem(
            'view-route',
            <InfoOutlinedIcon fontSize="small" />,
            'roads_importer_menu_view_route',
            onViewRoute
          ),
          renderRoutePickerItem(
            'edit-route',
            <EditOutlinedIcon fontSize="small" />,
            'roads_importer_menu_edit_route',
            onEditRoute
          ),
          <MenuItem
            key="edit-route-path"
            onClick={() => {
              if (contextMenu.route) onEditRoutePath(contextMenu.route);
              closeAll();
            }}
          >
            <ListItemIcon>
              <TimelineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t('roads_importer_menu_edit_route_path')}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="toggle-active-route"
            onClick={() => {
              if (contextMenu.route) onToggleRouteActive(contextMenu.route);
              closeAll();
            }}
          >
            <ListItemIcon>
              {contextMenu.route?.active ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {contextMenu.route?.active
                ? t('roads_importer_menu_deactivate_route')
                : t('roads_importer_menu_activate_route')}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="toggle-blocked-route"
            onClick={() => {
              if (!contextMenu.route) return;
              if (blockedInfo) {
                onUnblockRoute(contextMenu.route);
              } else {
                onBlockRoute(contextMenu.route);
              }
              closeAll();
            }}
          >
            <ListItemIcon>
              {blockedInfo ? (
                <LockOpenOutlinedIcon fontSize="small" />
              ) : (
                <BlockOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              sx={blockedInfo ? { color: 'error.main' } : undefined}
            >
              {blockedInfo
                ? t('roads_importer_menu_unblock_route')
                : t('roads_importer_menu_block_route')}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="delete-route"
            onClick={() => {
              onDeleteRoute(contextMenu.route);
              closeAll();
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>
              {t('roads_importer_menu_delete_route')}
            </ListItemText>
          </MenuItem>,
        ]
      ) : (
        <MenuItem
          onClick={() => {
            onAddLocation();
            closeAll();
          }}
        >
          <ListItemIcon>
            <AddLocationAltOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('roads_importer_menu_add_location')}</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
}
